import { combineLatest, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

@Component({
  templateUrl: './page-factory.component.html',
  styleUrls: ['./page-factory.component.scss'],
  standalone: false,
})
export class PageFactoryComponent implements OnInit, OnDestroy {
  public blockId: any;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);

  /** CB2 preview render target (root block pushed by the dashboard), reactive. */
  private readonly previewRootBlockId$ = toObservable(this.cb2PreviewState.rootBlockId);

  private readonly destroyed$ = new Subject<boolean>();

  constructor(private readonly activatedRoute: ActivatedRoute) {}

  ngOnInit(): void {
    combineLatest([this.activatedRoute.data, this.previewRootBlockId$])
      .pipe(
        takeUntil(this.destroyed$),
        tap(([data, previewRootBlockId]) => {
          // In CB2 preview, render the pushed draft root on the primary index
          // route only. Nested sibling `path: ''` routes must keep `data.block`
          // so they do not all swap to the same draft root (duplicate content).
          this.blockId = this.resolvePreviewBlockId(data, previewRootBlockId);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private resolvePreviewBlockId(
    data: { block?: string },
    previewRootBlockId: string | null,
  ): string | undefined {
    if (this.cb2PreviewState.isActive() && previewRootBlockId && this.isPrimaryPreviewRoute()) {
      return previewRootBlockId;
    }
    return data.block;
  }

  /** True when this route is the locale index, not a nested empty-path child. */
  private isPrimaryPreviewRoute(): boolean {
    const parentComponent = this.activatedRoute.parent?.snapshot.routeConfig?.component;
    return parentComponent !== PageFactoryComponent;
  }
}
